import React, { useState, useEffect, useRef, useMemo } from 'react'

import debounce from 'debounce'
import throttle from 'lodash.throttle'

import useSafeTimeout from './useSafeTimeout'

const normalizeOption = option =>
  typeof option == 'string' ? { name: option, value: option } : option

const ColorPicker = ({
  value, onChange, options, className = 'form-control', cols = 6,
  placeholder, ...props
}) => {
  const [ open, setOpen ] = useState(false)
  const [ dropup, setDropup ] = useState(false)

  const menuElement = useRef()
  const valueElement = useRef()
  const otherElement = useRef()
  const selectElement = useRef()
  const focusFirstOptionAfterOpen = useRef(false)

  const setSafeTimeout = useSafeTimeout()

  const normalizedOptions = useMemo(() =>
    options?.map(normalizeOption).filter(option => option && !option.disabled)
    || []
  , [ options ])

  const isHex = v => /^#([0-9a-f]{3}){1,2}$/i.test(v)
  const colorIsValid = isHex(value)
  // When empty, reflect an explicitly-provided placeholder color (an
  // inherited value) in the swatch so the preview isn't blank. Callers
  // that pass no placeholder keep an empty swatch — the "#000000" input
  // hint below is a text example, not a swatch color.
  const swatchColor = colorIsValid ? value : isHex(placeholder) ? placeholder : null

  useEffect(() => {
    const debounceDetermineDropDirection = debounce(determineDropDirection, 66)
    const throttleDetermineDropDirection = throttle(determineDropDirection, 66)
    addEventListener('resize', debounceDetermineDropDirection)
    addEventListener('scroll', throttleDetermineDropDirection)
    return () => {
      removeEventListener('resize', debounceDetermineDropDirection)
      removeEventListener('scroll', throttleDetermineDropDirection)
    }
  }, [])

  useEffect(() => {
    if (open) {
      determineDropDirection()

      if (focusFirstOptionAfterOpen.current)
        focusFirstOption()
    }
  }, [ open ])

  const determineDropDirection = () => {
    if (!valueElement.current) return
    const rect = valueElement.current.getBoundingClientRect()
    const bottom = rect.bottom + menuElement.current.offsetHeight + 2
    setDropup(bottom > document.documentElement.clientHeight)
  }

  const focusFirstOption = () => {
    focusFirstOptionAfterOpen.current = false
    const button = menuElement.current.querySelector('.color-option')
    || otherElement.current
    button.focus()
  }

  const handleBlur = () =>
    setSafeTimeout(() => {
      if (!selectElement.current.querySelector(':focus'))
        setOpen(false)
    }, 200)

  const handleFocus = () => setOpen(true)

  const handleChange = e => {
    const match = e.target.value.replace(/^#*([0-9a-f])/i, '#$1').match(/^#[0-9a-f]{0,6}/i)
    const value = match && match[0] || ''
    onChange({ ...e, target: { ...e.target, value } })
  }

  const handleClick = e => {
    onChange(e)
    valueElement.current.focus()
    setOpen(false)
  }

  const handleInputKeyDown = e => {
    switch (e.key) {
    case 'Escape':
      return setOpen(false)
    case 'ArrowDown':
      e.preventDefault()
      if (open)
        focusFirstOption()
      else {
        focusFirstOptionAfterOpen.current = true
        setOpen(true)
      }
    }
  }

  const handleOptionKeyDown = e => {
    switch (e.key) {
    case 'Escape':
      valueElement.current.focus()
      setOpen(false)
      return
    case 'ArrowUp':
    case 'ArrowDown':
    case 'ArrowLeft':
    case 'ArrowRight':
      e.preventDefault()
      const buttons = menuElement.current.querySelectorAll('.color-option')
      const button = e.target.closest('.color-option')
      const index = parseInt(button.dataset.index)

      if (e.key == 'ArrowUp' && index < cols)
        return valueElement.current.focus()
      if (
        e.key == 'ArrowDown' &&
        buttons.length < ((cols + index) - (index % cols) + 1)
      ) return otherElement.current.focus()

      const deltas = {
        ArrowUp: () => Math.max(0, index - cols),
        ArrowDown: () => Math.min(buttons.length - 1, index + cols),
        ArrowLeft: () => Math.max(0, index - 1),
        ArrowRight: () => Math.min(buttons.length - 1, index + 1),
      }
      const nextIndex = deltas[e.key]()
      buttons[nextIndex].focus()
    }
  }

  const handleOtherKeyDown = e => {
    switch (e.key) {
    case 'ArrowDown': return e.preventDefault()
    case 'ArrowUp':
      e.preventDefault()
      const buttons = menuElement.current.querySelectorAll('.color-option')
      const button = buttons[buttons.length - 1] || valueElement.current
      button.focus()
    }
  }

  return (
    <div
      ref={selectElement}
      className={[
        'text-left text-start',
        open ? 'open show' : '',
        dropup ? 'dropup' : 'dropdown',
      ].filter(Boolean).join(' ')}
      style={{ cursor: 'default' }}
      onBlur={handleBlur}
    >
      <input
        {...props}
        ref={valueElement}
        className={className}
        style={{ paddingLeft: '3.5rem', width: 'max(9rem, 140px)' }}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onClick={handleFocus}
        onKeyDown={handleInputKeyDown}
        placeholder={placeholder || '#000000'}
        pattern="#([0-9a-fA-F]{3}){1,2}"
        aria-expanded={open}
        autoComplete="off"
      />
        <div className="rounded-1 position-absolute top-0 start-0 pe-none" style={{
          backgroundColor: swatchColor,
          border: '1px solid rgb(0 0 0 / 0.1)',
          width: '2rem',
          height: '1.5rem',
          margin: 'calc(0.5rem - 1px) 0.5rem',
          transition: 'background-color 0.5s',
          // BS3
          position: 'absolute',
          top: '6px',
          left: '4px',
          pointerEvents: 'none',
        }}></div>
      <div
        ref={menuElement}
        className={`overflow-auto dropdown-menu p-2 ${open ? 'show' : ''}`}
        style={{
          zIndex: 1041,
          maxHeight: '33vh',
          // BS3
          padding: '8px',
        }}
        data-bs-popper
      >
        <ul
          className="list-unstyled mb-0 d-grid gap-2"
          style={{
            gridTemplateColumns: `repeat(${cols}, auto)`,
            // BS3
            display: 'grid',
            gap: '8px',
          }}
          onKeyDown={handleOptionKeyDown}
          aria-label="Suggested colors"
        >
          {normalizedOptions.map((option, index) => {
            const active = option.value.toLowerCase() == value.toLowerCase()
            return (
              <li
                key={option.value || 'blank'}
                className="d-flex rounded-1"
                style={{
                  boxShadow: active && '0 0 0 1px #fff, 0 0 0 3px var(--bs-primary)',
                  // BS3
                  display: 'flex',
                }}
              >
                <button
                  type="button"
                  tabIndex="-1"
                  className={`color-option form-control rounded-1 ${active ? 'active' : ''}`}
                  style={{
                    backgroundClip: 'border-box',
                    backgroundColor: option.value,
                    borderColor: 'rgb(0 0 0 / 0.1)',
                    width: '2rem',
                    height: '1.5rem',
                  }}
                  onClick={handleClick}
                  title={option.name}
                  value={option.value}
                  data-index={index}
                ></button>
              </li>
            )
          })}
        </ul>
        <div
          className={`position-relative p-0 ${normalizedOptions.length ? 'mt-2' : ''}`}
          style={{
            zIndex: 1,
            // BS3
            position: 'relative',
            marginTop: normalizedOptions.length ? '8px' : '',
            padding: '1px', // corresponding class in BS4/BS5 overrides this
          }}
        >
          <input
            ref={otherElement}
            type="color"
            tabIndex="-1"
            className="form-control position-absolute top-0 start-0 w-100 h-100"
            style={{
              zIndex: -1,
              cursor: 'pointer',
              // BS3
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            }}
            // Open the native picker at the shown swatch (current value,
            // else the inherited placeholder) rather than #000000 — and
            // never feed it '' , which is not a valid color input value
            // (React warns) and would silently reset it to black.
            value={swatchColor || '#000000'}
            onChange={onChange}
            onKeyDown={handleOtherKeyDown}
            aria-label="Other color"
          />
          <div
            className="bg-white rounded-2 p-1 pe-none"
            // BS3
            style={{
              backgroundColor: '#fff',
              pointerEvents: 'none',
              padding: '4px 8px',
            }}
            aria-hidden="true"
          >Other...</div>
        </div>
      </div>
    </div>
  )
}

export default ColorPicker
